import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';

import {
  Avatar,
  avatarType,
  Badge,
  badgeType,
  Breadcrumbs,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Icons,
  Text,
  useQuery,
} from '../../../packages';
import { getProfile } from '../../../redux/modules/profile/selectors';
import { profileActionCreators } from '../../../redux/modules/profile';
import { BaseLayout } from '../../../layouts/base';
import SelectedComponent from './ProviderDetail.selected';

import { headings, rightSideNavList } from './ProviderDetail.mock';
import { useStyles } from './ProviderDetail.styles';
import { scheduleActionCreators } from '../../../redux/modules/schedule';
import { fetchNavItemStatus } from '../../../services/datalab/datalab.service';
import { CardStatus } from '../../provider/profile-v2/common/card/profile-section-card';
import { ProfileInfoRole } from '../../../components/v2/ProfileInfo';

const ProviderDetail = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const query = useQuery();
  const navigate = useNavigate();
  const { providerId } = useParams();
  const { providers, providerDetails } = useSelector(getProfile);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [navItemStatus, setNavItemStatus] = useState(null);

  const getNavItemStatuses = async () => {
    const status = await fetchNavItemStatus({ providerId });
    const statusData = status.data.data;
    if (!providerDetails.active) {
      statusData.generalInformation = 'INACTIVE';
    }
    setNavItemStatus(statusData);
  };

  useEffect(() => {
    if (providerDetails) {
      void getNavItemStatuses();
    }
  }, [selectedScreen, providerDetails]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const getQuery = query.get('name');
    if (!getQuery) {
      query.set('name', 'general-information');
      window.location.search = query.toString();
    } else {
      setSelectedScreen(getQuery);
    }
  }, [query]);

  useEffect(() => {
    if (providerId) {
      dispatch(profileActionCreators.fetchProviderDetails(providerId));
      dispatch(scheduleActionCreators.fetchProviderSchedule({ userId: providerId }));
    } else {
      navigate('/admin/providers');
    }
  }, [providerId, providers]);

  const rightSideNavItems = useMemo(() => {
    const statusKeys = Object.keys(navItemStatus || {});
    // Filter navigation items based on user role
    const filteredNavList = rightSideNavList.filter(item => {
      // Hide MDToolbox Access for non-Nurse Practitioner and non-Physician roles
      if (item.query?.value === 'MDToolboxAccess') {
        return (
          providerDetails?.designation === ProfileInfoRole.NURSE_PRACTITIONER ||
          providerDetails?.designation === ProfileInfoRole.PHYSICIAN
        );
      }
      return true; // Show all other items
    });

    return filteredNavList.map(item => {
      if (item.children) return item;
      const navItem = statusKeys.find(key => key === item.query?.value);
      const status = navItemStatus?.[navItem] || '';
      switch (status) {
        case true:
          return {
            ...item,
            icon: 'checked-circle-outlined',
            activeIcon: 'checked-circle-outlined',
            classIcon: classes.successIcon,
          };
        case CardStatus.VERIFIED:
          return {
            ...item,
            icon: 'checked-circle-outlined',
            activeIcon: 'checked-circle-outlined',
            classIcon: classes.successIcon,
          };
        case 'COMPLETED':
          return {
            ...item,
            icon: 'checked-circle-outlined',
            activeIcon: 'checked-circle-outlined',
            classIcon: classes.successIcon,
          };
        case CardStatus.REVIEW_NEEDED:
          return {
            ...item,
            icon: 'question-outlined',
            activeIcon: 'question-outlined',
            classIcon: classes.infoIcon,
          };
        case CardStatus.DOCUMENT_ANALYSIS:
          return {
            ...item,
            icon: 'file-warning',
            activeIcon: 'file-warning',
            classIcon: classes.documentIcon,
          };
        case CardStatus.DOCUMENT_NEEDED:
          return {
            ...item,
            icon: 'file-warning',
            activeIcon: 'file-warning',
            classIcon: classes.documentIcon,
          };
        case CardStatus.DISCREPANCY:
          return {
            ...item,
            icon: 'alert-outlined',
            activeIcon: 'alert-outlined',
            classIcon: classes.discrepancyIcon,
          };
        case CardStatus.EXPIRED:
          return {
            ...item,
            icon: 'file-warning',
            activeIcon: 'file-warning',
            classIcon: classes.expiredIcon,
          };
        case CardStatus.EXPIRING_SOON:
          return {
            ...item,
            icon: 'file-warning',
            activeIcon: 'file-warning',
            classIcon: classes.expiringSoonIcon,
          };
        case CardStatus.MULTIPLE_STATUS:
          return {
            ...item,
            icon: 'alert-outlined',
            activeIcon: 'alert-outlined',
            classIcon: classes.multipleIcon,
          };
        case CardStatus.INCOMPLETE:
          return {
            ...item,
            icon: 'alert-outlined',
            activeIcon: 'alert-outlined',
            classIcon: classes.expiredIcon,
          };
        case CardStatus.FAILED:
          return {
            ...item,
            icon: 'alert-outlined',
            activeIcon: 'alert-outlined',
            classIcon: classes.expiredIcon,
          };
        case 'INACTIVE':
          return {
            ...item,
            icon: 'x-circle-outlined',
            activeIcon: 'x-circle-outlined',
            classIcon: classes.inactiveIcon,
          };
        default:
          return {
            ...item,
            icon: 'checked-circle-outlined',
            activeIcon: 'checked-circle-outlined',
            classIcon: classes.disabledIcon,
          };
      }
    });
  }, [rightSideNavList, navItemStatus]);

  const hasServiceAlerts =
    selectedScreen === 'services' &&
    !isLoading &&
    (!providerDetails.defaultInitialService || !providerDetails.defaultFollowupService);
  return (
    <BaseLayout
      rightSideNavList={rightSideNavItems}
      rightSideBarWidth="320px"
      rightSidebarHeadCustom={
        <Box className={classes.head}>
          <Avatar
            src={providerDetails?.profileImage}
            name={providerDetails?.fullName}
            variant={avatarType.CIRCLE}
            size={40}
          />
          <Box>
            <Text className={classes.name}>{providerDetails?.fullName}</Text>
            <Badge variant={badgeType.FILLED} className={clsx(classes.badge, providerDetails?.designation)}>
              {providerDetails?.designation}
            </Badge>
          </Box>
        </Box>
      }
    >
      <div className={clsx(classes.root, { [classes.rootWithAlerts]: hasServiceAlerts })}>
        <Box className={classes.header}>
          <Breadcrumbs
            links={[
              { href: '/admin/providers', text: 'Providers' },
              { text: isLoading ? '' : providerDetails?.fullName },
            ]}
          />
        </Box>
        {headings[selectedScreen] && (
          <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
            {headings[selectedScreen]}
          </Heading>
        )}
        {hasServiceAlerts && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {!providerDetails.defaultInitialService && (
              <Box className={classes.alertWrapper}>
                <Icons glyph="info-outlined" color={colors.destructive500} />
                <Text className={classes.alertText}>Default initial service not set</Text>
              </Box>
            )}
            {!providerDetails.defaultFollowupService && (
              <Box className={classes.alertWrapper}>
                <Icons glyph="info-outlined" color={colors.destructive500} />
                <Text className={classes.alertText}>Default follow-up service not set</Text>
              </Box>
            )}
          </Box>
        )}
        <SelectedComponent
          selectedScreen={selectedScreen}
          navItemStatus={navItemStatus}
          setNavItemStatus={setNavItemStatus}
          userId={providerId}
          refresh={() => void getNavItemStatuses()}
        />
      </div>
    </BaseLayout>
  );
};

export { ProviderDetail };
